import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Box, Select, TextButton } from '@zillow/constellation-10';
import { format } from 'date-fns';
import { GraphWrapper } from '../../../../../SharedComponents/GraphWrapper';
import { CustomGraphTooltip } from '../../../../../SharedComponents/CustomGraphTooltip';
import { CustomGraphLegend } from '../../../../../SharedComponents/CustomGraphLegend';
import { MonthAndYearCustomTick } from '../../../../../SharedComponents/MonthAndYearCustomTick';
import { getGraphTitle } from '../../../../../../utils/getGraphTitle';
import { useMainLayout } from '../../../../../layouts/MainPageLayout/MainLayoutContext';
import { formatLocalDate } from '../../../../../../utils/localDateToDate';
import type { PropertyOption, PropertyViewCompsChartDataPoint } from './RentComps.types';

type PropertyViewCompsGraphProps = {
  chartData: Array<PropertyViewCompsChartDataPoint>;
  averageViewRanking: number;
  isLoading: boolean;
  selectedPropertyName: string;
  allCompNames: Array<string>;
  allCompsColorMapping: Record<string, string>;
  propertyOptions: Array<PropertyOption>;
  selectedUnitId: number | null;
  setSelectedUnitId: (value: number | null) => void;
  onEditCompetitors: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RankDot = (props: any) => {
  const { cx, cy, value, stroke } = props;
  if (cx === undefined || cy === undefined || value === null || value === undefined) return null;

  return (
    <g>
      <circle cx={cx} cy={cy} r={10} fill={stroke} />
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={11} fontWeight="bold">
        {value}
      </text>
    </g>
  );
};

export const PropertyViewCompsGraph = ({
  chartData,
  averageViewRanking,
  isLoading,
  selectedPropertyName,
  allCompNames,
  allCompsColorMapping,
  propertyOptions,
  selectedUnitId,
  setSelectedUnitId,
  onEditCompetitors,
}: PropertyViewCompsGraphProps) => {
  const {
    filteredProperties,
    regionsMapping,
    selectedRegionId,
    selectedFilters,
    isFetchingInitialData,
    analyticsLastUpdatedDates,
  } = useMainLayout();

  const lastUpdatedDate = formatLocalDate(analyticsLastUpdatedDates?.portfolioRentLastUpdated);
  const currentDateFormatted = format(new Date(), 'MMM yyyy');

  const title = getGraphTitle({
    graphName: 'Property view comps',
    regionsMapping,
    selectedRegionId,
    selectedFilters,
    filteredProperties,
  });

  const allSeriesNames = selectedPropertyName ? [selectedPropertyName, ...allCompNames] : allCompNames;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const labelTypes: Record<string, any> = {};
  allSeriesNames.forEach((name) => {
    labelTypes[name] = 'line';
  });
  labelTypes['Month to date'] = 'dashed-line';

  return (
    <GraphWrapper
      graphId="property-view-comps"
      title={title}
      subtitle={`How your property views rank compared to your competitors.${lastUpdatedDate ? ` Last updated ${lastUpdatedDate}.` : ''}`}
      loading={isLoading || isFetchingInitialData}
      infoCards={[
        {
          title: 'Average view ranking',
          infoText: 'Average property view ranking for your portfolio in the selected market, month to date.',
          value: averageViewRanking,
        },
      ]}
      headerAction={
        <TextButton onClick={onEditCompetitors} css={{ color: 'text.link' }}>
          Edit competitors
        </TextButton>
      }
      customFilters={
        <Box css={{ display: 'flex', gap: '200' }}>
          <Select
            size="md"
            value={String(selectedUnitId ?? '')}
            onChange={(e) => setSelectedUnitId(Number(e.target.value))}
            css={{ maxWidth: '250px' }}
          >
            {propertyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Box>
      }
    >
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <YAxis domain={[1, 6]} allowDecimals={false} reversed />
          <XAxis dataKey="timestamp" interval={0} tick={<MonthAndYearCustomTick />} />
          <Tooltip
            content={
              <CustomGraphTooltip
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (filteredPayload: Array<any>, displayLabel: string) => {
                    const isCurrentMonth = displayLabel === currentDateFormatted;
                    if (isCurrentMonth) {
                      return filteredPayload.filter(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) => entry.dataKey?.endsWith(' month to date'),
                      );
                    }
                    return filteredPayload.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (entry: any) => !entry.dataKey?.endsWith(' month to date'),
                    );
                  }
                }
              />
            }
          />
          <Legend
            wrapperStyle={{ marginBottom: '-32px' }}
            content={
              <CustomGraphLegend
                labelTypes={labelTypes}
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (payload: Array<any>) => {
                    const filtered = payload.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (entry: any) => !entry.value?.endsWith(' month to date'),
                    );
                    const order = [...allSeriesNames, 'Month to date'];
                    return filtered.sort(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (a: any, b: any) => order.indexOf(a.value) - order.indexOf(b.value),
                    );
                  }
                }
              />
            }
          />
          {allSeriesNames.map((name) => (
            <Line
              key={name}
              type="linear"
              dataKey={name}
              stroke={allCompsColorMapping[name] || '#000'}
              strokeWidth={3}
              dot={<RankDot />}
            />
          ))}
          {allSeriesNames.map((name) => (
            <Line
              key={`${name}-mtd`}
              type="linear"
              dataKey={`${name} month to date`}
              stroke={allCompsColorMapping[name] || '#000'}
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={<RankDot />}
              legendType="none"
            />
          ))}
          <Line type="linear" dataKey="Month to date" stroke="#666666" strokeDasharray="5 5" dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </GraphWrapper>
  );
};
